import { z } from 'zod';

/**
 * A página de apresentação do sistema (ADR 0118): a página pública que o endereço do sistema mostra
 * a quem ainda não entrou. Mora no `visual.json`, campo `apresentacao`, que o Smart Builder preenche
 * com o modelo escolhido pelo cliente. Vazio (a base), o endereço abre direto a tela de entrar.
 *
 * Os textos são do cliente (não passam pela tradução); as chaves seguem o resto do visual.json.
 */

const line = (max: number) => z.string().trim().min(1).max(max);

const item = z.object({ titulo: line(80), texto: z.string().trim().max(400).default('') });

/** Seções na ordem em que aparecem. */
export const presentationSectionSchema = z.discriminatedUnion('tipo', [
  /** Cartões lado a lado: serviços, produtos, diferenciais. */
  z.object({ tipo: z.literal('destaques'), titulo: line(80), itens: z.array(item).min(1).max(6) }),
  /** Um bloco de texto: quem somos, nossa história. */
  z.object({ tipo: z.literal('texto'), titulo: line(80), texto: line(1500) }),
  /** Passos numerados: como funciona, como pedir. */
  z.object({ tipo: z.literal('passos'), titulo: line(80), itens: z.array(item).min(2).max(6) }),
  /** Perguntas que abrem a resposta. */
  z.object({ tipo: z.literal('perguntas'), titulo: line(80), itens: z.array(item).min(1).max(10) }),
]);
export type PresentationSection = z.infer<typeof presentationSectionSchema>;

/** Só dígitos, com DDI (5531999999999), para o link do WhatsApp e do telefone. */
const digits = z
  .string()
  .trim()
  .regex(/^\d{10,15}$/);

export const presentationContactSchema = z
  .object({
    whatsapp: digits.optional(),
    telefone: digits.optional(),
    email: z.email().max(200).optional(),
    endereco: z.string().trim().min(1).max(200).optional(),
    horario: z.string().trim().min(1).max(200).optional(),
  })
  .refine((c) => Object.values(c).some(Boolean), { message: 'contato vazio' });
export type PresentationContact = z.infer<typeof presentationContactSchema>;

export const PRESENTATION_HEROES = ['centro', 'lado'] as const;

export const presentationSchema = z.object({
  /** Abertura: o título grande, a frase de apoio e o botão de chamada. */
  titulo: line(100),
  texto: z.string().trim().max(400).default(''),
  chamada: line(40),
  /** Abertura centralizada ou com o logo grande ao lado. */
  abertura: z.enum(PRESENTATION_HEROES).default('centro'),
  secoes: z.array(presentationSectionSchema).max(8).default([]),
  contato: presentationContactSchema.optional(),
});
export type Presentation = z.infer<typeof presentationSchema>;

/** A apresentação gravada no visual.json, ou null se não há (ou está fora do formato). */
export function presentationOf(value: unknown): Presentation | null {
  if (value == null) return null;
  const parsed = presentationSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Para onde vai o botão de chamada: o WhatsApp, se houver, senão o contato da própria página. */
export function presentationCallTarget(p: Presentation): string | null {
  if (p.contato?.whatsapp) return whatsappUrl(p.contato.whatsapp);
  if (p.contato) return '#contato';
  return null;
}

export function whatsappUrl(number: string): string {
  return `https://wa.me/${number}`;
}
